import { z } from 'zod/v4';

import { ExampleAppDomainSchema } from './exampleApp';
import { GuardrailsModeSchema } from './guardrailsConfig';
import { MediaStorageConfigSchema } from './mediaStorage';
import { TargetUsageSchema } from './metrics';

export enum RoleManagementMode {
  PROMPTFOO = 'promptfoo',
  TEAM_ONLY = 'team_only',
  IDP = 'idp',
}

// Organization name validation - prevents XSS and ensures reasonable names
const organizationNameValidator = z
  .string()
  .min(1, 'Name is required')
  .max(200, 'Name must be 200 characters or less')
  .trim()
  .refine((name) => {
    // Allow alphanumeric, spaces, hyphens, underscores, periods, apostrophes, ampersands, and parentheses
    // This prevents most XSS attempts while allowing reasonable organization names
    const validPattern = /^[a-zA-Z0-9\s\-_.,'&()]+$/;
    return validPattern.test(name);
  }, 'Name contains invalid characters');

// Email domain validation - ensures valid domain format
const emailDomainValidator = z
  .string()
  .min(1, 'Domain cannot be empty')
  .max(253, 'Domain must be 253 characters or less')
  .toLowerCase()
  .trim()
  .refine((domain) => {
    // Valid domain pattern: alphanumeric with hyphens, separated by dots
    // Examples: example.com, sub.example.co.uk
    const validPattern =
      /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*$/;
    return validPattern.test(domain);
  }, 'Invalid email domain format');

// Array of email domains with max limit
const allowableEmailDomainsValidator = z
  .array(emailDomainValidator)
  .max(100, 'Cannot have more than 100 email domains');

export const UpdateOrganizationSchema = z.object({
  name: organizationNameValidator,
  excludeTargetOutputFromAgenticAttackGeneration: z.boolean().optional(),
  roleManagementMode: z.nativeEnum(RoleManagementMode).optional(),
});

export const PatchOrganizationSchema = z.object({
  name: organizationNameValidator.optional(),
  excludeTargetOutputFromAgenticAttackGeneration: z.boolean().optional(),
  roleManagementMode: z.nativeEnum(RoleManagementMode).optional(),
  defaultTeamOnlyRoleId: z.string().nullable().optional(),
  mediaStorageEnabled: z.boolean().optional(),
  mediaStorageConfig: MediaStorageConfigSchema.nullable().optional(),
});

export const AdminCreateOrganizationSchema = z.object({
  name: organizationNameValidator,
  canUseRedteam: z.boolean().optional(),
  canUseAdaptiveGuardrails: z.boolean().optional(),
  createExampleApp: z.boolean().optional(),
  exampleAppDomain: ExampleAppDomainSchema.optional(),
});

export const AdminUpdateOrganizationSchema = UpdateOrganizationSchema.extend({
  name: organizationNameValidator.optional(),
  canUseRedteam: z.boolean().optional(),
  excludeTargetOutputFromAgenticAttackGeneration: z.boolean().optional(),
  canUseAdaptiveGuardrails: z.boolean().optional(),
  roleManagementMode: z.nativeEnum(RoleManagementMode).optional(),
  allowableEmailDomains: allowableEmailDomainsValidator.optional(),
  defaultTeamOnlyRoleId: z.string().nullable().optional(),
  mediaStorageEnabled: z.boolean().optional(),
  mediaStorageConfig: MediaStorageConfigSchema.nullable().optional(),
});

const [
  InlineMediaStorageSchema,
  S3MediaStorageSchema,
  GCSMediaStorageSchema,
  AzureMediaStorageSchema,
] = MediaStorageConfigSchema.options;

/** Provider fields preserved by Organization.serialize(), including redaction markers. */
export const SanitizedMediaStorageConfigSchema = z.discriminatedUnion('provider', [
  InlineMediaStorageSchema,
  S3MediaStorageSchema.extend({
    config: S3MediaStorageSchema.shape.config.extend({
      secretAccessKey: z
        .enum(['***', ''])
        .describe('Redaction marker for the secret access key, or an empty string when unset.'),
    }),
  }),
  GCSMediaStorageSchema.extend({
    config: GCSMediaStorageSchema.shape.config.omit({ credentials: true }),
  }),
  AzureMediaStorageSchema.extend({
    config: AzureMediaStorageSchema.shape.config.omit({ connectionString: true }).extend({
      accountKey: z
        .literal('***')
        .optional()
        .describe('Redaction marker when an account key is set.'),
    }),
  }),
]);

export const OrganizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  canUseRedteam: z.boolean(),
  canUseAdaptiveGuardrails: z.boolean(),
  excludeTargetOutputFromAgenticAttackGeneration: z.boolean(),
  idpEnabled: z.boolean(),
  roleManagementMode: z.nativeEnum(RoleManagementMode),
  allowableEmailDomains: allowableEmailDomainsValidator,
  defaultTeamOnlyRoleId: z.string().nullable(),
  // Media storage settings
  mediaStorageEnabled: z.boolean().optional(),
  mediaStorageConfigured: z.boolean().optional(),
  mediaStorageConfig: SanitizedMediaStorageConfigSchema.nullable().optional(),
  // Guardrails API version mode (determines which UI/API endpoints are available)
  guardrailsMode: GuardrailsModeSchema.optional(),
});

export const OrganizationResponseSchema = OrganizationSchema.extend({
  updatedAt: z.iso.datetime().nullable(),
  userCount: z.number().optional(),
  lastActivityAt: z.iso.datetime().optional(),
});

/** Organization list responses retain the historical deletion timestamp. */
export const OrganizationListItemSchema = OrganizationSchema.extend({
  deletedAt: z.iso.datetime().nullable(),
});

export const SetUserAdminStatusResponseSchema = z.object({
  isAdmin: z.boolean(),
});

export const OrganizationUsageSchema = z.object({
  id: z.string(),
  targets: z.array(TargetUsageSchema),
});

export type UpdateOrganizationRequest = z.infer<typeof UpdateOrganizationSchema>;
export type PatchOrganizationRequest = z.infer<typeof PatchOrganizationSchema>;
export type AdminUpdateOrganizationRequest = z.infer<typeof AdminUpdateOrganizationSchema>;
export type OrganizationDTO = z.infer<typeof OrganizationSchema>;
export type OrganizationListItemDTO = z.infer<typeof OrganizationListItemSchema>;
export type OrganizationResponseDTO = z.infer<typeof OrganizationResponseSchema>;
export type OrganizationUsageDTO = z.infer<typeof OrganizationUsageSchema>;

// New schemas
export const CreateOrganizationSchema = z.object({
  name: organizationNameValidator,
  createExampleApp: z.boolean().optional(),
  exampleAppDomain: ExampleAppDomainSchema.optional(),
});

export const AddUserToOrganizationSchema = z.object({
  email: z.string().email('Invalid email format'),
  name: z.string().optional(),
  teamId: z.string().min(1, 'Team ID cannot be empty').optional(),
  roleId: z.string().min(1, 'Role ID cannot be empty').optional(),
});

export const SetUserAdminStatusSchema = z.object({
  isAdmin: z.boolean(),
});

export const CreateServiceAccountSchema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
});

// Service account response schemas
export const ServiceAccountSchema = z.object({
  id: z.string(),
  name: z.string(),
  token: z.string(),
  createdAt: z.iso.datetime(),
});

export const ServiceAccountListItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.iso.datetime(),
  isAdmin: z.boolean(),
  teams: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
    }),
  ),
});

// Request types
export type CreateOrganizationRequest = z.infer<typeof CreateOrganizationSchema>;
export type AddUserToOrganizationRequest = z.infer<typeof AddUserToOrganizationSchema>;
export type SetUserAdminStatusRequest = z.infer<typeof SetUserAdminStatusSchema>;
export type CreateServiceAccountRequest = z.infer<typeof CreateServiceAccountSchema>;

// Response types
export type SetUserAdminStatusResponseDTO = z.infer<typeof SetUserAdminStatusResponseSchema>;
export type ServiceAccountDTO = z.infer<typeof ServiceAccountSchema>;
export type ServiceAccountListItemDTO = z.infer<typeof ServiceAccountListItemSchema>;
